import { Stack, Typography } from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { SubpageLayout } from '@/components/architecture/Layout/SubpageLayout';
import { Button } from '@/components/form/baseInputs/Button';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { DataTableCard } from '@/components/tables/DataTable/components/DataTableCard';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { TableEmptyState } from '@/components/tables/DataTable/TableEmptyState';
import { useReportError } from '@/hooks/useReportError';
import {
  ABOUT_ESTATE_PLANNING_ITEMS,
  EducationListItem,
  EducationListItemType,
  PLANNING_BASICS_CATEGORY,
  PLANNING_STRATEGIES_CATEGORY,
  PLANNING_STRATEGIES_ITEMS,
  STATE_INHERITANCE_TAX_CATEGORY,
  STATE_TAX_ITEMS,
  TAX_TOPICS_CATEGORY,
  TAX_TOPICS_ITEMS,
} from '@/modules/education/Education.constants';
import { DRAFT_SLIDE_ID } from '@/modules/education/Education.sentinels';
import { getCustomSlideEducationCategory } from '@/modules/education/Education.utils';
import {
  CustomSlide,
  EducationSlideOverride,
  EducationSlideOverridesProvider,
  isEducationPageOrganizationManaged,
  useEducationMap,
  useEducationSlideOverrides,
} from '@/modules/education/EducationSlideOverrides/EducationSlideOverrides.context';
import { EducationSlideTrowser } from '@/modules/education/EducationSlideTrowser/EducationSlideTrowser';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import {
  parseStack,
  serializeStack,
  TROWSER_PARAM,
  TrowserKey,
} from '@/modules/navigation/trowsers/trowser.types';
import { TenantPresentationConfigurationProvider } from '@/modules/presentation/context/TenantPresentationConfiguration.provider';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { TenantOrgMembershipType } from '@/types/schema';

import { useEducationPagesTableColumns } from './AdminEducationPagesTab.utils';

export interface RowData {
  id: string;
  name: string;
  category: string;
  createdByDisplayName?: string;
  isCustomPage?: boolean;
  /** Slide identifier or education ID of the source slide this was duplicated from */
  duplicatedFrom?: string;
  isOrganizationManaged?: boolean;
}

/**
 * Converts a CustomSlide to a RowData entry for the table.
 */
function customSlideToRow(slide: CustomSlide): RowData {
  const contentJSON = slide.contentJSON as unknown as Record<string, unknown>;
  return {
    id: slide.slideIdentifier,
    name: slide.displayName || 'Untitled',
    category: getCustomSlideEducationCategory(slide) ?? 'Custom',
    createdByDisplayName: slide.createdByDisplayName,
    isCustomPage: true,
    duplicatedFrom:
      typeof contentJSON?.duplicatedFrom === 'string'
        ? contentJSON.duplicatedFrom
        : undefined,
  };
}

function mapEducationDataToRows(
  customSlidesMap: Record<string, CustomSlide>,
  educationMap: Record<EducationListItemType, EducationListItem>,
  overridesMap: Record<string, EducationSlideOverride>
): RowData[] {
  const rows: RowData[] = [];

  // Add Planning basics items.
  ABOUT_ESTATE_PLANNING_ITEMS.forEach((itemId) => {
    const educationItem = educationMap[itemId];
    rows.push({
      id: itemId,
      name: educationItem.title,
      category: PLANNING_BASICS_CATEGORY,
      isOrganizationManaged: isEducationPageOrganizationManaged(
        itemId,
        overridesMap
      ),
    });
  });

  // Add "Planning strategies" items
  PLANNING_STRATEGIES_ITEMS.forEach((itemId) => {
    const educationItem = educationMap[itemId];
    rows.push({
      id: itemId,
      name: educationItem.title,
      category: PLANNING_STRATEGIES_CATEGORY,
      isOrganizationManaged: isEducationPageOrganizationManaged(
        itemId,
        overridesMap
      ),
    });
  });

  // Add "Tax topics" items
  TAX_TOPICS_ITEMS.forEach((itemId) => {
    const educationItem = educationMap[itemId];
    rows.push({
      id: itemId,
      name: educationItem.title,
      category: TAX_TOPICS_CATEGORY,
      isOrganizationManaged: isEducationPageOrganizationManaged(
        itemId,
        overridesMap
      ),
    });
  });

  STATE_TAX_ITEMS.forEach((itemId) => {
    const educationItem = educationMap[itemId];
    rows.push({
      id: itemId,
      name: educationItem.title,
      category: STATE_INHERITANCE_TAX_CATEGORY,
      isOrganizationManaged: isEducationPageOrganizationManaged(
        itemId,
        overridesMap
      ),
    });
  });

  // Separate custom slides into those with a duplicatedFrom source and those without.
  // The duplicatedFrom field in contentJSON tracks which slide this was duplicated from,
  // and persists through renames so ordering is not dependent on the slide name.
  const customSlides = Object.values(customSlidesMap);
  const slidesWithSource: CustomSlide[] = [];
  const slidesWithoutSource: CustomSlide[] = [];
  for (const slide of customSlides) {
    const cj = slide.contentJSON as unknown as Record<string, unknown>;
    if (typeof cj?.duplicatedFrom === 'string') {
      slidesWithSource.push(slide);
    } else {
      slidesWithoutSource.push(slide);
    }
  }

  // Add non-duplicate custom slides at the end
  slidesWithoutSource.forEach((slide) => rows.push(customSlideToRow(slide)));

  // Insert duplicate slides after their source rows using the duplicatedFrom field.
  // Uses multiple passes to handle chains (e.g., duplicate of a duplicate).
  let remaining = [...slidesWithSource];
  let prevRemainingCount = remaining.length + 1;
  while (remaining.length > 0 && remaining.length < prevRemainingCount) {
    prevRemainingCount = remaining.length;
    const notYetInserted: typeof remaining = [];
    for (const slide of remaining) {
      const row = customSlideToRow(slide);
      const sourceIndex = rows.findIndex((r) => r.id === row.duplicatedFrom);
      if (sourceIndex !== -1) {
        // Insert after the source and any existing duplicates of the same source
        let insertAt = sourceIndex + 1;
        while (
          insertAt < rows.length &&
          rows[insertAt]?.duplicatedFrom === row.duplicatedFrom
        ) {
          insertAt++;
        }
        rows.splice(insertAt, 0, row);
      } else {
        notYetInserted.push(slide);
      }
    }
    remaining = notYetInserted;
  }

  // Add any remaining duplicate slides that couldn't be positioned (source not found)
  remaining.forEach((slide) => rows.push(customSlideToRow(slide)));

  return rows;
}

/** Rewrites the old /education/:slideId route to the URL-backed trowser. */
function useMigrateLegacyEducationSlideRoute(slideId?: string) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!slideId) return;

    const searchParams = new URLSearchParams(location.search);
    const stack = parseStack(searchParams.get(TROWSER_PARAM)).filter(
      (entry) => entry.key !== TrowserKey.EducationPages
    );
    stack.push({ key: TrowserKey.EducationPages, target: slideId });
    searchParams.set(TROWSER_PARAM, serializeStack(stack));

    void navigate(
      {
        pathname: getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_EDUCATION, {}),
        search: searchParams.toString(),
      },
      { replace: true, state: location.state }
    );
  }, [location.search, location.state, navigate, slideId]);
}

function AdminEducationPageContent() {
  const educationTrowser = useTrowser(TrowserKey.EducationPages);
  const { slideId: legacySlideId } = useParams<{ slideId?: string }>();
  useMigrateLegacyEducationSlideRoute(legacySlideId);

  const handleNavigateToSlide = useCallback(
    (newSlideId: string) => {
      educationTrowser.open({ target: newSlideId });
    },
    [educationTrowser]
  );

  const {
    loading,
    error: educationPagesError,
    customSlidesMap,
    overridesMap,
    tenantOrgMembershipType,
  } = useEducationSlideOverrides();
  useReportError('Failed to load Education pages', educationPagesError);
  const educationMap = useEducationMap();
  const isParentTenant =
    tenantOrgMembershipType === TenantOrgMembershipType.Parent;
  const columns = useEducationPagesTableColumns(
    handleNavigateToSlide,
    isParentTenant
  );

  const selectedSlideId = educationTrowser.target as
    | EducationListItemType
    | undefined;

  const rows = useMemo(
    () => mapEducationDataToRows(customSlidesMap, educationMap, overridesMap),
    [customSlidesMap, educationMap, overridesMap]
  );

  const isTrowserOpen =
    educationTrowser.isOpen && Boolean(educationTrowser.target);

  const handleRowClick = (params: GridRowParams<RowData>) => {
    educationTrowser.open({ target: params.row.id });
  };

  const handleCloseTrowser = () => {
    educationTrowser.close();
  };

  const handleAddNewClick = () => {
    educationTrowser.open({ target: DRAFT_SLIDE_ID });
  };

  const handleSlideCreated = (slideIdentifier: string) => {
    educationTrowser.open({ target: slideIdentifier });
  };

  if (educationPagesError) {
    return (
      <SubpageLayout heading="Education pages">
        <Typography variant="body-s">
          The Education pages could not be loaded. Please try again later.
        </Typography>
      </SubpageLayout>
    );
  }

  return (
    <SubpageLayout
      heading="Education pages"
      actions={
        <Button
          size="sm"
          variant="secondary"
          startIcon={PlusIcon}
          onClick={handleAddNewClick}
        >
          New page
        </Button>
      }
    >
      <DataTableCard>
        <Stack spacing={3}>
          <Stack spacing={1}>
            <DataTable
              columns={columns}
              rows={rows}
              loading={loading}
              hideFooterPagination
              pagination={false}
              onRowClick={handleRowClick}
              slots={{
                noRowsOverlay: () => (
                  <TableEmptyState text="No education pages" />
                ),
              }}
            />
          </Stack>
        </Stack>
      </DataTableCard>

      {isTrowserOpen && (
        <TenantPresentationConfigurationProvider subBrandId={null}>
          <EducationSlideTrowser
            isOpen
            onClose={handleCloseTrowser}
            educationSlideId={selectedSlideId ?? null}
            onSlideCreated={handleSlideCreated}
          />
        </TenantPresentationConfigurationProvider>
      )}
    </SubpageLayout>
  );
}

export function AdminEducationPage() {
  return (
    <EducationSlideOverridesProvider mode="management">
      <AdminEducationPageContent />
    </EducationSlideOverridesProvider>
  );
}
